import { useEffect, useState } from 'react';
import { Alert, Box, Button, List, ListItem, TextField, Typography } from '@mui/material';
import type { SupportedLocale } from '@stm/contracts';
import { translate } from '@stm/i18n';
import { Link, useNavigate } from 'react-router-dom';
import type { AccountActions, AccountState, Landing } from './account';
import { ApiRequestError } from './api';
import { readPendingInvitation, rememberPendingInvitation } from './pendingInvitation';

/** Answers after which the same code can never be accepted, so it is not offered again. */
const finalAcceptanceStatuses: ReadonlySet<number> = new Set([403, 404, 409, 410]);

/**
 * The invitation token travels in the link's fragment, which browsers never send to servers, so it
 * stays out of access logs and referrers. It is removed from the address bar once read, and kept in
 * browser storage for at most an hour while the person signs in.
 */
function readInvitationToken(): string {
  if (typeof window === 'undefined') return '';
  const fromFragment = new URLSearchParams(window.location.hash.slice(1)).get('token');
  if (fromFragment !== null && fromFragment !== '') {
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
    return fromFragment;
  }
  return readPendingInvitation();
}

export interface PanelProps {
  locale: SupportedLocale;
  account: AccountState;
  actions: AccountActions;
}

export function InvitePanel({ locale, account, actions }: PanelProps): React.JSX.Element {
  const navigate = useNavigate();
  const [token, setToken] = useState(readInvitationToken);
  const [errorKey, setErrorKey] = useState<ApiRequestError['messageKey'] | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    rememberPendingInvitation(token.trim());
  }, [token]);

  async function accept(): Promise<void> {
    setBusy(true);
    setErrorKey(null);
    try {
      await actions.acceptInvitation(token.trim());
      rememberPendingInvitation('');
      void navigate('/', { replace: true });
    } catch (error) {
      setErrorKey(error instanceof ApiRequestError ? error.messageKey : 'error.unexpected');
      // A used, withdrawn or expired code must not bring the person back here after every sign-in.
      if (error instanceof ApiRequestError && finalAcceptanceStatuses.has(error.status)) {
        rememberPendingInvitation('');
      }
    } finally {
      setBusy(false);
    }
  }

  const signInPath = `/api/v1/auth/login?locale=${locale}&returnTo=%2Finvite`;

  return (
    <Box sx={{ mb: 3, display: 'grid', gap: 2, maxWidth: 560 }}>
      {errorKey !== null && <Alert severity="error">{translate(locale, errorKey)}</Alert>}
      <TextField
        label={translate(locale, 'invite.tokenLabel')}
        value={token}
        onChange={(event) => {
          setToken(event.target.value);
        }}
        autoComplete="off"
        spellCheck={false}
        fullWidth
      />
      {account.status === 'signedIn' ? (
        <Button
          variant="contained"
          disabled={busy || token.trim() === ''}
          onClick={() => void accept()}
        >
          {translate(locale, 'invite.accept')}
        </Button>
      ) : (
        <>
          <Typography variant="body2" color="text.secondary">
            {translate(locale, 'invite.accountHint')}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Button variant="contained" component="a" href={signInPath}>
              {translate(locale, 'invite.signInToAccept')}
            </Button>
            <Button variant="outlined" component="a" href={`${signInPath}&createAccount=1`}>
              {translate(locale, 'invite.createAccount')}
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
}

export function LandingPanel({
  locale,
  landing,
  account,
  actions,
}: PanelProps & { landing: Landing }): React.JSX.Element | null {
  const [errorKey, setErrorKey] = useState<ApiRequestError['messageKey'] | null>(null);
  switch (landing.kind) {
    case 'wait':
      return <Typography>{translate(locale, 'account.loading')}</Typography>;
    case 'unavailable':
      return <Alert severity="error">{translate(locale, 'error.unexpected')}</Alert>;
    case 'noAccess':
      return (
        <Box sx={{ mb: 3, display: 'grid', gap: 2, justifyItems: 'start' }}>
          <Alert severity="info">{translate(locale, 'account.noMembership')}</Alert>
          <Button variant="contained" component={Link} to="/invite">
            {translate(locale, 'account.openInvitation')}
          </Button>
        </Box>
      );
    case 'choose':
      return (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" component="h2">
            {translate(locale, 'account.chooseOrganization')}
          </Typography>
          {errorKey !== null && <Alert severity="error">{translate(locale, errorKey)}</Alert>}
          <List>
            {account.status === 'signedIn' &&
              account.me.memberships.map((membership) => (
                <ListItem key={membership.organizationId} disableGutters>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setErrorKey(null);
                      actions
                        .selectOrganization(membership.organizationId)
                        .catch((error: unknown) => {
                          setErrorKey(
                            error instanceof ApiRequestError
                              ? error.messageKey
                              : 'error.unexpected',
                          );
                        });
                    }}
                  >
                    {membership.organizationName}
                  </Button>
                </ListItem>
              ))}
          </List>
        </Box>
      );
    case 'login':
    case 'home':
      // Both redirect (see the shell); nothing to show for the moment it takes.
      return null;
  }
}
